import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { SGA_CLASSES } from './Products';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { logAudit } from './auditLog';
import UpgradeLimitModal from './components/UpgradeLimitModal';
import { nivelProductos, paresConConflicto, NivelCompat } from './lib/compatibilidad';
import { ubicacionesHabilitadas } from './lib/ubicaciones-flag';
import { sugerirCodigoZona } from './lib/ubicaciones-codigos';
import { mensajeAmigable } from './lib/ubicaciones';
import ZonaRacksModal from './components/ubicaciones/ZonaRacksModal';
import { useSedes } from './lib/useSedes';
import { etiquetaSede, matrizDe, opcionesSede } from './lib/sedes';
import './Warehouses.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Warehouse {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  site_id?: string | null;   // M40: sede de la bodega (sus zonas y racks la heredan)
}

interface Zone {
  id: string;
  warehouse_id: string;
  name: string;
  product_ids: string[];
  created_at: string;
  code?: string | null;
}

interface Product {
  id: string;
  name: string;
  sga_classes: string[];
}

// ── Compatibilidad: tabla única (src/lib/compatibilidad.ts) ─────────────────
// En esta pantalla el nivel 'precaucion' se sigue llamando 'segregar'
// internamente porque nombra clases CSS (.wh-alert.segregar).

type ZoneLevel = 'compatible' | 'segregar' | 'incompatible';
const toZoneLevel = (n: NivelCompat): ZoneLevel => (n === 'precaucion' ? 'segregar' : n);

interface CompatResult { status: 'ok' | 'segregar' | 'incompatible'; conflicts: string[]; }

function checkZoneCompat(prods: Product[]): CompatResult {
  const conflicts: string[] = [];
  let worst: CompatResult['status'] = 'ok';

  for (const { a, b, nivel } of paresConConflicto(prods)) {
    if (nivel === 'incompatible') worst = 'incompatible';
    else if (worst === 'ok') worst = 'segregar';
    const label = nivel === 'incompatible'
      ? `${a.name} + ${b.name}`
      : `${a.name} ↔ ${b.name}`;
    if (!conflicts.includes(label)) conflicts.push(label);
  }

  return { status: worst, conflicts };
}

function productCompatInZone(a: Product, b: Product): ZoneLevel | 'self' {
  if (a.id === b.id) return 'self';
  return toZoneLevel(nivelProductos(a, b));
}

// ── Empty form states ─────────────────────────────────────────────────────────

const EMPTY_WH   = { name: '', description: '', site_id: '' };
const EMPTY_ZONE = { name: '', product_ids: [] as string[], code: '' };

// ── Component ─────────────────────────────────────────────────────────────────

export default function Warehouses() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const effectiveCompany   = impersonated ?? company;
  const effectiveCompanyId = effectiveCompany?.id ?? null;
  const maxWarehouses = effectiveCompany?.max_warehouses ?? null;
  // Ubicaciones (racks/niveles/posiciones): solo is_dev hasta que JC la libere.
  const conUbicaciones = ubicacionesHabilitadas(effectiveCompany);
  const rol = profile?.role;
  const puedeEditarUbic = rol === 'admin' || rol === 'manager' || rol === 'superadmin';
  const puedeBorrarUbic = rol === 'admin' || rol === 'superadmin';
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [zoneCounts, setZoneCounts] = useState<Record<string, number>>({});
  const [products,   setProducts]   = useState<Product[]>([]);
  const [loading,    setLoading]    = useState(true);

  // navigation
  const [selectedWH,   setSelectedWH]   = useState<Warehouse | null>(null);
  const [zones,         setZones]         = useState<Zone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);

  // warehouse modal
  const [whModalOpen, setWhModalOpen] = useState(false);
  const [whEditing,   setWhEditing]   = useState<Warehouse | null>(null);
  const [whForm,      setWhForm]      = useState(EMPTY_WH);
  const [whSaving,    setWhSaving]    = useState(false);
  const [whError,     setWhError]     = useState('');

  // zone modal
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [zoneEditing,   setZoneEditing]   = useState<Zone | null>(null);
  const [zoneForm,      setZoneForm]      = useState(EMPTY_ZONE);
  const [zoneSaving,    setZoneSaving]    = useState(false);
  const [zoneError,     setZoneError]     = useState('');
  const [prodSearch,    setProdSearch]    = useState('');

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast,      setToast]      = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [fetchError,  setFetchError]  = useState('');
  const [zonesError,  setZonesError]  = useState('');

  // zone matrix modal
  const [zoneMatrixOpen, setZoneMatrixOpen] = useState(false);
  const [zoneMatrixZone, setZoneMatrixZone] = useState<Zone | null>(null);

  // racks de una zona
  const [racksZone, setRacksZone] = useState<Zone | null>(null);

  // F1a: sede (campo del formulario y filtro) solo con 2 o más sedes activas; con una, la pantalla no cambia.
  const { sedes, varias: conSede } = useSedes(effectiveCompanyId);
  const [filtroSede, setFiltroSede] = useState('');
  const sedeMap = new Map(sedes.map(s => [s.id, s]));

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    const [{ data: whs, error: e1 }, { data: zs, error: e2 }, { data: prods, error: e3 }] = await Promise.all([
      effectiveCompanyId
        ? supabase.from('warehouses').select('*').eq('company_id', effectiveCompanyId).order('created_at', { ascending: false })
        : supabase.from('warehouses').select('*').order('created_at', { ascending: false }),
      // warehouse_zones no tiene company_id: se filtra por la empresa de su bodega.
      effectiveCompanyId
        ? supabase.from('warehouse_zones').select('id, warehouse_id, warehouses!inner(company_id)').eq('warehouses.company_id', effectiveCompanyId)
        : supabase.from('warehouse_zones').select('id, warehouse_id'),
      effectiveCompanyId
        ? supabase.from('products').select('id, name, sga_classes').eq('company_id', effectiveCompanyId).order('name')
        : supabase.from('products').select('id, name, sga_classes').order('name'),
    ]);
    if (e1 || e2 || e3) {
      setFetchError('Error al cargar bodegas. Intenta de nuevo.');
      setLoading(false);
      return;
    }
    setWarehouses(whs ?? []);
    setProducts(prods ?? []);
    const counts: Record<string, number> = {};
    (zs ?? []).forEach((z: { id: string; warehouse_id: string }) => {
      counts[z.warehouse_id] = (counts[z.warehouse_id] ?? 0) + 1;
    });
    setZoneCounts(counts);
    setLoading(false);
  }, [effectiveCompanyId]);

  const fetchZones = useCallback(async (whId: string) => {
    setZonesLoading(true);
    setZonesError('');
    const { data, error: err } = await supabase
      .from('warehouse_zones')
      .select('*')
      .eq('warehouse_id', whId)
      .order('created_at', { ascending: true });
    if (err) setZonesError('Error al cargar zonas. Intenta de nuevo.');
    else setZones(data ?? []);
    setZonesLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (selectedWH) fetchZones(selectedWH.id); }, [selectedWH, fetchZones]);

  // ── Toast ─────────────────────────────────────────────────────────────────

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Warehouse modal ───────────────────────────────────────────────────────

  const openWhAdd = () => { setWhEditing(null); setWhForm({ ...EMPTY_WH, site_id: matrizDe(sedes)?.id ?? '' }); setWhError(''); setWhModalOpen(true); };

  const openWhEdit = (wh: Warehouse) => {
    setWhEditing(wh);
    setWhForm({ name: wh.name, description: wh.description ?? '', site_id: wh.site_id ?? '' });
    setWhError('');
    setWhModalOpen(true);
  };

  const closeWhModal = () => { setWhModalOpen(false); setWhEditing(null); setWhForm(EMPTY_WH); setWhError(''); };

  const saveWarehouse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whForm.name.trim()) { setWhError('El nombre es obligatorio.'); return; }
    // warehouses.company_id es obligatorio (migración 20261014050000).
    if (!whEditing && !effectiveCompanyId) { setWhError('Selecciona una empresa antes de crear una bodega.'); return; }
    setWhSaving(true); setWhError('');
    const payload: any = { name: whForm.name.trim(), description: whForm.description.trim() || null };
    if (!whEditing) payload.company_id = effectiveCompanyId;
    // Solo con el selector visible; con una sede el payload es el de siempre y la base pone la Matriz.
    if (conSede && whForm.site_id) payload.site_id = whForm.site_id;

    let savedId = whEditing?.id ?? '';
    if (whEditing) {
      const { error } = await supabase.from('warehouses').update(payload).eq('id', whEditing.id);
      if (error) { setWhError('Error al guardar: ' + error.message); setWhSaving(false); return; }
    } else {
      const { data, error } = await supabase.from('warehouses').insert(payload).select('id').single();
      if (error) { setWhError('Error al guardar: ' + error.message); setWhSaving(false); return; }
      savedId = data?.id ?? '';
    }
    if (effectiveCompanyId && savedId) logAudit({ company_id: effectiveCompanyId, action: whEditing ? 'updated' : 'created', entity_type: 'warehouse', entity_id: savedId, entity_name: payload.name });
    showToast(whEditing ? 'Bodega actualizada.' : 'Bodega creada.'); closeWhModal(); fetchAll();
    setWhSaving(false);
  };

  const deleteWarehouse = async (id: string) => {
    setDeletingId(id);
    const wh = warehouses.find(w => w.id === id);
    const { error } = await supabase.from('warehouses').delete().eq('id', id);
    if (error) { showToast('Error al eliminar: ' + error.message); }
    else {
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'deleted', entity_type: 'warehouse', entity_id: id, entity_name: wh?.name ?? id });
      showToast('Bodega eliminada.'); setWarehouses((p) => p.filter((w) => w.id !== id));
    }
    setDeletingId(null);
  };

  // ── Zone modal ────────────────────────────────────────────────────────────

  const openZoneAdd = () => { setZoneEditing(null); setZoneForm({ ...EMPTY_ZONE, code: conUbicaciones ? sugerirCodigoZona(zones.map(z => z.code)) : '' }); setZoneError(''); setProdSearch(''); setZoneModalOpen(true); };

  const openZoneEdit = (z: Zone) => {
    setZoneEditing(z);
    setZoneForm({ name: z.name, product_ids: z.product_ids ?? [], code: z.code ?? '' });
    setZoneError(''); setProdSearch(''); setZoneModalOpen(true);
  };

  const closeZoneModal = () => { setZoneModalOpen(false); setZoneEditing(null); setZoneForm(EMPTY_ZONE); setZoneError(''); setProdSearch(''); };

  const toggleProduct = (id: string) =>
    setZoneForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id],
    }));

  const saveZone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zoneForm.name.trim()) { setZoneError('El nombre es obligatorio.'); return; }
    if (!selectedWH) return;
    setZoneSaving(true); setZoneError('');
    const payload: { name: string; product_ids: string[]; warehouse_id: string; code?: string | null } =
      { name: zoneForm.name.trim(), product_ids: zoneForm.product_ids, warehouse_id: selectedWH.id };
    // El código corto solo se envía con ubicaciones habilitadas (clientes reales: payload sin cambios).
    if (conUbicaciones) payload.code = zoneForm.code.trim() || null;

    let savedZoneId = zoneEditing?.id ?? '';
    if (zoneEditing) {
      const { error } = await supabase.from('warehouse_zones').update(payload).eq('id', zoneEditing.id);
      if (error) { setZoneError('Error al guardar: ' + mensajeAmigable(error.message)); setZoneSaving(false); return; }
    } else {
      const { data, error } = await supabase.from('warehouse_zones').insert(payload).select('id').single();
      if (error) { setZoneError('Error al guardar: ' + mensajeAmigable(error.message)); setZoneSaving(false); return; }
      savedZoneId = data?.id ?? '';
    }
    if (effectiveCompanyId && savedZoneId) logAudit({ company_id: effectiveCompanyId, action: zoneEditing ? 'updated' : 'created', entity_type: 'zone', entity_id: savedZoneId, entity_name: payload.name, details: { warehouse_name: selectedWH.name, product_count: payload.product_ids.length } });
    showToast(zoneEditing ? 'Zona actualizada.' : 'Zona creada.'); closeZoneModal(); fetchZones(selectedWH.id); fetchAll();
    setZoneSaving(false);
  };

  const deleteZone = async (id: string) => {
    if (!selectedWH) return;
    setDeletingId(id);
    const zone = zones.find(z => z.id === id);
    const { error } = await supabase.from('warehouse_zones').delete().eq('id', id);
    if (error) { showToast('Error al eliminar: ' + error.message); }
    else {
      if (effectiveCompanyId) logAudit({ company_id: effectiveCompanyId, action: 'deleted', entity_type: 'zone', entity_id: id, entity_name: zone?.name ?? id, details: { warehouse_name: selectedWH.name } });
      showToast('Zona eliminada.'); setZones((p) => p.filter((z) => z.id !== id)); fetchAll();
    }
    setDeletingId(null);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getZoneProds = (zone: Zone) => products.filter((p) => zone.product_ids.includes(p.id));

  const filteredProds = products.filter((p) =>
    p.name.toLowerCase().includes(prodSearch.toLowerCase())
  );

  // ── Render: Zones view ────────────────────────────────────────────────────

  if (selectedWH) {
    return (
      <div className="wh-root">
        <div className="wh-breadcrumb">
          <button className="wh-back-btn" onClick={() => setSelectedWH(null)}>
            <BackIcon /> Bodegas
          </button>
          <span className="wh-bc-sep">/</span>
          <span className="wh-bc-current">{selectedWH.name}</span>
        </div>

        <div className="wh-header">
          <div>
            <h3 className="wh-title">{selectedWH.name}</h3>
            <p className="wh-subtitle">
              {selectedWH.description || 'Sin descripción'} · {zones.length} zona{zones.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button className="wh-btn-add" onClick={openZoneAdd}>
            <PlusIcon /> Agregar Zona
          </button>
        </div>

        {zonesLoading ? (
          <div className="wh-loading"><span className="wh-spinner" /><p>Cargando zonas…</p></div>
        ) : zonesError ? (
          <div className="pr-error-box">
            <p>{zonesError}</p>
            <button className="pr-btn-retry" onClick={() => fetchZones(selectedWH.id)}>Reintentar</button>
          </div>
        ) : zones.length === 0 ? (
          <div className="wh-empty">
            <EmptyZoneIcon />
            <p className="wh-empty-title">Sin zonas</p>
            <p className="wh-empty-sub">Crea una zona para organizar los productos de esta bodega.</p>
          </div>
        ) : (
          <div className="wh-zones-grid">
            {zones.map((zone) => {
              const zProds = getZoneProds(zone);
              const compat = checkZoneCompat(zProds);
              return (
                <div
                  key={zone.id}
                  className={`wh-zone-card ${compat.status === 'incompatible' ? 'danger' : compat.status === 'segregar' ? 'warn' : ''}`}
                >
                  <div className="wh-zone-top">
                    <div className="wh-zone-name-row">
                      <ZoneIcon />
                      <span className="wh-zone-name">{zone.name}</span>
                      {conUbicaciones && zone.code && <span className="wh-zone-code">{zone.code}</span>}
                      <span className="wh-zone-prod-count">{zProds.length} prod.</span>
                    </div>
                    <div className="wh-actions">
                      <button
                        className="wh-btn-icon wh-btn-view"
                        onClick={() => { setZoneMatrixZone(zone); setZoneMatrixOpen(true); }}
                        title="Ver matriz de compatibilidad"
                      >
                        <EyeIcon />
                      </button>
                      <button className="wh-btn-icon wh-btn-edit" onClick={() => openZoneEdit(zone)} title="Editar zona">
                        <EditIcon />
                      </button>
                      <button
                        className="wh-btn-icon wh-btn-delete"
                        onClick={() => deleteZone(zone.id)}
                        disabled={deletingId === zone.id}
                        title="Eliminar zona"
                      >
                        {deletingId === zone.id ? <span className="wh-spinner wh-spinner-sm" /> : <TrashIcon />}
                      </button>
                    </div>
                  </div>

                  {compat.status !== 'ok' && (
                    <div className={`wh-alert ${compat.status}`}>
                      <AlertIcon />
                      <div className="wh-alert-body">
                        <span className="wh-alert-title">
                          {compat.status === 'incompatible' ? 'Productos INCOMPATIBLES' : 'Precaución: separar físicamente según la ficha de datos de seguridad (SDS)'}
                        </span>
                        <span className="wh-alert-detail">{compat.conflicts.join(' · ')}</span>
                      </div>
                    </div>
                  )}

                  {conUbicaciones && (
                    <button className="wh-btn-racks" onClick={() => setRacksZone(zone)}>Racks y ubicaciones</button>
                  )}

                  {zProds.length === 0 ? (
                    <p className="wh-zone-no-prods">Sin productos asignados</p>
                  ) : (
                    <div className="wh-zone-prods">
                      {zProds.map((prod) => (
                        <div key={prod.id} className="wh-prod-row">
                          <span className="wh-prod-name">{prod.name}</span>
                          <div className="wh-badges">
                            {prod.sga_classes.map((id) => {
                              const cls = SGA_CLASSES.find((s) => s.id === id);
                              return cls ? (
                                <span key={id} className="wh-badge"
                                  style={{ color: cls.color, background: cls.bg, borderColor: cls.color + '40' }}
                                  title={cls.label}
                                >
                                  {id.replace('clase', 'C')}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {toast && <div className="wh-toast">{toast}</div>}

        {racksZone && (() => { const zActual = zones.find(z => z.id === racksZone.id) ?? racksZone; return (
          <ZonaRacksModal
            zone={{ id: zActual.id, name: zActual.name, code: zActual.code ?? null, product_ids: zActual.product_ids ?? [] }}
            products={products}
            puedeEditar={puedeEditarUbic}
            puedeBorrar={puedeBorrarUbic}
            onClose={() => setRacksZone(null)}
            onZonaCambiada={() => { fetchZones(selectedWH.id); fetchAll(); }}
          />
        ); })()}

        {/* ── Zone matrix modal ── */}
        {zoneMatrixOpen && zoneMatrixZone && (() => {
          const zProds = getZoneProds(zoneMatrixZone);
          return (
            <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && setZoneMatrixOpen(false)}>
              <div className="wh-modal wh-modal-matrix">
                <div className="wh-modal-header">
                  <h4>Compatibilidad · {zoneMatrixZone.name}</h4>
                  <button className="wh-modal-close" onClick={() => setZoneMatrixOpen(false)}><CloseIcon /></button>
                </div>
                <div className="wh-matrix-body">
                  {zProds.length < 2 ? (
                    <p className="wh-zone-no-prods" style={{ padding: '24px 0', textAlign: 'center' }}>
                      {zProds.length === 0
                        ? 'Sin productos asignados en esta zona.'
                        : 'Se necesitan al menos 2 productos para generar la matriz.'}
                    </p>
                  ) : (
                    <>
                      <div className="wh-matrix-wrap">
                        <table className="wh-matrix-table">
                          <thead>
                            <tr>
                              <th className="wh-mth-corner" />
                              {zProds.map(p => (
                                <th key={p.id} className="wh-mth-col">
                                  <div className="wh-mcol-label" title={p.name}>{p.name}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {zProds.map(row => (
                              <tr key={row.id}>
                                <td className="wh-mtd-row"><span title={row.name}>{row.name}</span></td>
                                {zProds.map(col => {
                                  const lv = productCompatInZone(row, col);
                                  if (lv === 'self') return (
                                    <td key={col.id} className="wh-mcell wh-mcell-self">
                                      <span className="wh-micon">—</span>
                                    </td>
                                  );
                                  const cfg = {
                                    compatible:   { cls: 'wh-mcell-ok',   icon: '✓' },
                                    segregar:     { cls: 'wh-mcell-warn', icon: '!' },
                                    incompatible: { cls: 'wh-mcell-bad',  icon: '✕' },
                                  }[lv];
                                  return (
                                    <td key={col.id} className={`wh-mcell ${cfg.cls}`}>
                                      <span className="wh-micon">{cfg.icon}</span>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="wh-matrix-legend">
                        <span className="wh-mleg-item wh-mleg-bad">✕ Incompatible</span>
                        <span className="wh-mleg-item wh-mleg-warn">! Precaución</span>
                        <span className="wh-mleg-item wh-mleg-ok">✓ Compatible</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Zone modal ── */}
        {zoneModalOpen && (
          <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && closeZoneModal()}>
            <div className="wh-modal wh-modal-zone">
              <div className="wh-modal-header">
                <h4>{zoneEditing ? 'Editar Zona' : 'Agregar Zona'}</h4>
                <button className="wh-modal-close" onClick={closeZoneModal}><CloseIcon /></button>
              </div>

              <form className="wh-form" onSubmit={saveZone}>
                <div className="wh-field">
                  <label>Nombre de la zona <span className="wh-required">*</span></label>
                  <input
                    type="text"
                    placeholder="ej. Zona A · Inflamables"
                    value={zoneForm.name}
                    onChange={(e) => setZoneForm((f) => ({ ...f, name: e.target.value }))}
                    autoFocus
                  />
                </div>

                {conUbicaciones && (
                  <div className="wh-field">
                    <label>Código de zona (opcional)</label>
                    <input
                      type="text"
                      placeholder="ej. Z1"
                      maxLength={10}
                      value={zoneForm.code}
                      onChange={(e) => setZoneForm((f) => ({ ...f, code: e.target.value }))}
                    />
                  </div>
                )}

                <div className="wh-field">
                  <label>
                    Productos asignados
                    {zoneForm.product_ids.length > 0 && (
                      <span className="wh-sel-count">{zoneForm.product_ids.length} seleccionado{zoneForm.product_ids.length !== 1 ? 's' : ''}</span>
                    )}
                  </label>
                  <input
                    type="text"
                    className="wh-search"
                    placeholder="Buscar producto…"
                    value={prodSearch}
                    onChange={(e) => setProdSearch(e.target.value)}
                  />
                  <div className="wh-prod-list">
                    {filteredProds.length === 0 ? (
                      <p className="wh-no-prods">
                        {products.length === 0 ? 'Sin productos registrados en el sistema.' : 'Sin resultados para esa búsqueda.'}
                      </p>
                    ) : filteredProds.map((prod) => {
                      const checked = zoneForm.product_ids.includes(prod.id);
                      return (
                        <label key={prod.id} className={`wh-prod-item ${checked ? 'checked' : ''}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleProduct(prod.id)} />
                          <span className="wh-prod-item-name">{prod.name}</span>
                          <div className="wh-badges">
                            {prod.sga_classes.map((id) => {
                              const cls = SGA_CLASSES.find((s) => s.id === id);
                              return cls ? (
                                <span key={id} className="wh-badge"
                                  style={{ color: cls.color, background: cls.bg, borderColor: cls.color + '40' }}
                                  title={cls.label}
                                >
                                  {id.replace('clase', 'C')}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {zoneError && <p className="wh-error">{zoneError}</p>}

                <div className="wh-modal-footer">
                  <button type="button" className="wh-btn-cancel" onClick={closeZoneModal}>Cancelar</button>
                  <button type="submit" className="wh-btn-save" disabled={zoneSaving}>
                    {zoneSaving && <span className="wh-spinner wh-spinner-sm" />}
                    {zoneSaving ? 'Guardando…' : (zoneEditing ? 'Guardar cambios' : 'Agregar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render: Warehouses list ───────────────────────────────────────────────

  const whAtLimit = maxWarehouses !== null && warehouses.length >= maxWarehouses;
  const whVisibles = conSede && filtroSede ? warehouses.filter(w => w.site_id === filtroSede) : warehouses;

  return (
    <div className="wh-root">
      <div className="wh-header">
        <div>
          <h3 className="wh-title">Bodegas</h3>
          <p className="wh-subtitle">
            {warehouses.length} bodega{warehouses.length !== 1 ? 's' : ''} registrada{warehouses.length !== 1 ? 's' : ''}
            {maxWarehouses !== null && (
              <span className={`wh-limit-tag ${whAtLimit ? 'at-limit' : ''}`}>
                {' '}· {warehouses.length}/{maxWarehouses}
              </span>
            )}
          </p>
        </div>
        <button
          className="wh-btn-add"
          onClick={() => { if (whAtLimit) setShowUpgradeModal(true); else openWhAdd(); }}
          title={whAtLimit ? `Límite del plan alcanzado (${maxWarehouses} bodegas)` : undefined}
        >
          <PlusIcon /> Agregar Bodega
        </button>
      </div>

      {showUpgradeModal && (
        <UpgradeLimitModal
          resource="warehouses"
          currentUsage={warehouses.length}
          plan={effectiveCompany?.plan}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {whAtLimit && (
        <div className="wh-limit-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <div>
            <strong>Límite del plan alcanzado</strong>
            <p>Has alcanzado el máximo de {maxWarehouses} bodegas. Actualiza tu plan para agregar más.</p>
          </div>
        </div>
      )}

      {conSede && (
        <div className="wh-field" style={{ maxWidth: 320, marginBottom: 12 }}>
          <select value={filtroSede} onChange={(e) => setFiltroSede(e.target.value)} aria-label="Filtrar por sede">
            <option value="">Sede: todas</option>
            {opcionesSede(sedes).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      <div className="wh-table-wrap">
        {loading ? (
          <div className="wh-loading"><span className="wh-spinner" /><p>Cargando bodegas…</p></div>
        ) : fetchError ? (
          <div className="pr-error-box">
            <p>{fetchError}</p>
            <button className="pr-btn-retry" onClick={fetchAll}>Reintentar</button>
          </div>
        ) : warehouses.length === 0 ? (
          <div className="wh-empty">
            <EmptyWhIcon />
            <p className="wh-empty-title">Sin bodegas</p>
            <p className="wh-empty-sub">Haz clic en "Agregar Bodega" para comenzar.</p>
          </div>
        ) : (
          <table className="wh-table">
            <thead>
              <tr>
                <th>Nombre</th>
                {conSede && <th>Sede</th>}
                <th>Descripción</th>
                <th style={{ width: 110 }}>Zonas</th>
                <th style={{ width: 110 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {whVisibles.map((wh) => (
                <tr key={wh.id} className="wh-row" onClick={() => setSelectedWH(wh)}>
                  <td className="wh-td-name">
                    <div className="wh-name-cell">
                      <WhSmIcon />
                      {wh.name}
                    </div>
                  </td>
                  {conSede && (
                    <td className="wh-td-desc">
                      {wh.site_id && sedeMap.get(wh.site_id) ? etiquetaSede(sedeMap.get(wh.site_id)!) : <span className="wh-empty-cell">—</span>}
                    </td>
                  )}
                  <td className="wh-td-desc">{wh.description || <span className="wh-empty-cell">—</span>}</td>
                  <td>
                    <span className="wh-zone-pill">
                      {zoneCounts[wh.id] ?? 0} zona{(zoneCounts[wh.id] ?? 0) !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="wh-actions">
                      <button className="wh-btn-icon wh-btn-edit" onClick={() => openWhEdit(wh)} title="Editar">
                        <EditIcon />
                      </button>
                      <button
                        className="wh-btn-icon wh-btn-delete"
                        onClick={() => deleteWarehouse(wh.id)}
                        disabled={deletingId === wh.id}
                        title="Eliminar"
                      >
                        {deletingId === wh.id ? <span className="wh-spinner wh-spinner-sm" /> : <TrashIcon />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && <div className="wh-toast">{toast}</div>}

      {/* ── Warehouse modal ── */}
      {whModalOpen && (
        <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && closeWhModal()}>
          <div className="wh-modal">
            <div className="wh-modal-header">
              <h4>{whEditing ? 'Editar Bodega' : 'Agregar Bodega'}</h4>
              <button className="wh-modal-close" onClick={closeWhModal}><CloseIcon /></button>
            </div>

            <form className="wh-form" onSubmit={saveWarehouse}>
              <div className="wh-field">
                <label>Nombre <span className="wh-required">*</span></label>
                <input
                  type="text"
                  placeholder="ej. Bodega Principal"
                  value={whForm.name}
                  onChange={(e) => setWhForm((f) => ({ ...f, name: e.target.value }))}
                  autoFocus
                />
              </div>

              <div className="wh-field">
                <label>Descripción</label>
                <input
                  type="text"
                  placeholder="ej. Almacén de químicos área norte"
                  value={whForm.description}
                  onChange={(e) => setWhForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>

              {conSede && (
                <div className="wh-field">
                  <label htmlFor="wh-sede">Sede</label>
                  <select id="wh-sede" value={whForm.site_id} onChange={(e) => setWhForm((f) => ({ ...f, site_id: e.target.value }))}>
                    {opcionesSede(sedes, whEditing?.site_id).map(s => <option key={s.id} value={s.id}>{etiquetaSede(s)}</option>)}
                  </select>
                </div>
              )}

              {whError && <p className="wh-error">{whError}</p>}

              <div className="wh-modal-footer">
                <button type="button" className="wh-btn-cancel" onClick={closeWhModal}>Cancelar</button>
                <button type="submit" className="wh-btn-save" disabled={whSaving}>
                  {whSaving && <span className="wh-spinner wh-spinner-sm" />}
                  {whSaving ? 'Guardando…' : (whEditing ? 'Guardar cambios' : 'Agregar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ZoneIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function WhSmIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function EmptyWhIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function EmptyZoneIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
